/**
 * Shared React Query helpers for the admin panel.
 *
 * - extractErrorMessage: pulls a human-readable message from axios/fetch errors
 * - useAdminMutation: wraps useMutation with useNotify() toast on error/success
 *
 * @module hooks/queries/useAdminQuery
 */

import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { useNotify } from 'react-admin';

/**
 * Extract a human-readable error message from an axios error, fetch Response, or generic Error.
 */
export function extractErrorMessage(error: unknown): string {
  if (!error) return 'An unknown error occurred';

  // Axios error shape. The backend's error handler replies
  // { code, httpStatusCode, userMessage, developerMessage } — there is no
  // `message` field — so reading only `message` showed axios' own "Request
  // failed with status code 409" for every server-side refusal (e.g. "email is
  // already registered as a customer"). userMessage first; developerMessage is
  // present only where IS_DEVELOPER_MESSAGE_ENABLED is on (local).
  if (typeof error === 'object' && error !== null) {
    const axiosErr = error as {
      response?: {
        data?: { message?: string; userMessage?: string; developerMessage?: string };
        status?: number;
      };
      message?: string;
    };
    const data = axiosErr.response?.data;
    const generic = /^(Conflict|Bad request|Forbidden|Unauthorized|Not found|Internal server error)$/i;
    if (data?.userMessage && !generic.test(data.userMessage)) return data.userMessage;
    if (data?.developerMessage) return data.developerMessage;
    if (data?.message) return data.message;
    if (data?.userMessage) return data.userMessage;
    if (axiosErr.message) return axiosErr.message;
  }

  if (error instanceof Error) return error.message;
  return String(error);
}

type AdminMutationOptions<TData, TVariables> = Omit<
  UseMutationOptions<TData, unknown, TVariables>,
  'mutationFn'
> & {
  mutationFn: (variables: TVariables) => Promise<TData>;
  successMessage?: string | ((data: TData) => string);
};

/**
 * Wraps useMutation with automatic react-admin useNotify() toasts.
 *
 * On error: shows extractErrorMessage(error) as 'error' toast.
 * On success: shows successMessage (if provided) as 'success' toast.
 */
export function useAdminMutation<TData = unknown, TVariables = void>(
  options: AdminMutationOptions<TData, TVariables>,
) {
  const notify = useNotify();
  const { successMessage, onError, onSuccess, ...rest } = options;

  return useMutation<TData, unknown, TVariables>({
    ...rest,
    onSuccess: (data, variables, onMutateResult, context) => {
      if (successMessage) {
        const msg = typeof successMessage === 'function' ? successMessage(data) : successMessage;
        notify(msg, { type: 'success' });
      }
      onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      notify(extractErrorMessage(error), { type: 'error' });
      onError?.(error, variables, onMutateResult, context);
    },
  });
}
